import { DeleteOutlined, EditOutlined, EditTwoTone, FormOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Card, Input, Modal, Select, Switch, Table } from 'antd'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AdvertiserBean, ItemResponseAdvertiserBeanObject, ListPagination, MediaAgencyBean } from 'src/api/api'
import { useUser } from 'src/context/UserContext'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import { StorageKey, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { useDebounce } from 'src/util/useDebounce'
import { toSort, useSorter } from 'src/util/useSorter'
import { isAdmin, isAgencyAdmin } from 'src/util/Util'
import advertiserApi from './AdvertiserApi'

const Column = Table.Column
const Option = Select.Option

const storageWriter = storageWriterFactory('advertiser')
const storageReader = storageReaderFactory('advertiser')

interface Props { }

const AdvertiserList: React.FC<Props> = () => {

    const formInfoItems: FormDataType[] = []

    const [data, setData] = useState<AdvertiserBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [info, setInfo] = useState({
        mediaAgencies: []
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const [mediaAgency, setMediaAgency] = useState<number>()
    const [active, setActive] = useState<boolean>()

    const { user } = useUser()

    const admin = isAdmin(user)
    const agencyAdmin = isAgencyAdmin(user)

    const remove = (record: AdvertiserBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                await advertiserApi.delete(record.id as number)
                let page = pagination.current
                if (data.length == 1 && pagination.current > 1) {
                    page -= 1
                }
                loadData(page)
            }
        })
    }

    const activate = async (record: AdvertiserBean, index) => {
        await advertiserApi.activate(record.id)

        const bean = data[index]
        bean.active = !record.active

        setData([...data])
    }

    const loadData = async (page: number) => {

        const response = await advertiserApi.list(pagination.defaultPageSize, page, toSort(sorter), search, mediaAgency, active)

        const array = response.data as Array<ItemResponseAdvertiserBeanObject>
        const advertiserBeans: AdvertiserBean[] = array.map(item => item.data as AdvertiserBean)

        if (advertiserBeans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage
            })

            setData(advertiserBeans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    const loadInfo = async () => {
        if (admin || agencyAdmin) formInfoItems.push(FormDataType.MediaAgencies)
        const resp = await formDataLoader(...formInfoItems)
        setInfo(resp as any)
    }

    useEffect(() => {
        loadInfo()
    }, [])

    useEffect(() => {
        const searchParams = storageReader.read(StorageKey.SearchParams)
        const page = searchParams.page || pagination.current

        loadData(page)
    }, [debouncedSearch, mediaAgency, active, sorter])

    return <Card
        title="Advertiser"
        className="list-card"
        extra={<Link to={`/advertiser/create`} className="create-new-link">
            <Button type="primary" htmlType="button" icon={<PlusOutlined />}>
                New Advertiser
            </Button>
        </Link>}
    >
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name" onChange={e => setSearch(e.target.value || undefined)} />
            {(admin || agencyAdmin) && <Select placeholder="Filter by MediaAgency" onChange={setMediaAgency} allowClear>
                {info.mediaAgencies.map((item: MediaAgencyBean) => <Option key={item.id} value={item.id}>{item.name}</Option>)}
            </Select>}
            <Select placeholder="Filter by Active" onChange={setActive} allowClear>
                {Object.values(['Active', 'Inactive']).map(type => <Option key={type} value={type == 'Active'}>{type}</Option>)}
            </Select>
        </div>
        <Table
            rowKey={'id'}
            size={'middle'}
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column key="id" title="ID" dataIndex="id" sorter />
            <Column key="name" title="Name" dataIndex="name" sorter render={(text, record: AdvertiserBean) => <Link to={`/advertiser/${record.id}/edit`}>{text}</Link>} />
            {(admin || agencyAdmin) && <Column
                key="mediaAgencyId"
                title="Media Agency"
                dataIndex="mediaAgencyId"
                render={id => (info.mediaAgencies as MediaAgencyBean[]).find(item => id == item.id)?.name}
            />}
            <Column key="domain" title="Domain" dataIndex="domain" />
            <Column
                align="center"
                key="active"
                title="Active"
                dataIndex="active"
                render={(text, record: AdvertiserBean, index) => <Switch checked={record.active} size="small" onChange={e => activate(record, index)} />}
            />
            <Column align="center" key="action" title="Actions" render={(text: string, record: AdvertiserBean) =>
                <span>
                    <Link to={`/advertiser/${record.id}/edit`}>
                        <Button type="text" >
                            <FormOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            } />
        </Table>
    </Card>
}

export default AdvertiserList


// WEBPACK FOOTER //
// ./src/component/app/advertiser/AdvertiserList.tsx